import { ATTENDEE_TYPES } from 'src/config/constants'
import type {
  IAttendance,
  IAttendanceNode,
  IAttendee,
  IAttendeeNode,
  ITeam,
  ITeamNode,
} from 'src/interfaces/attendance'

export const generateAttendanceNode = (atte: IAttendance) => {
  const attendStaffIds: string[] = []
  const attendStudentIds: string[] = []
  const attendanceNode: IAttendanceNode = {
    id: atte.id,
    label: atte.space.name,
    color: atte.space.color,
    spaceId: atte.space.id,
    spaceType: atte.space.type,
    taskId: atte.space.taskId,
    attendanceId: atte.id,
    selected: false,
    disabled: false,
    type: 'space',
    license:
      atte.permittedUse && atte.permittedUse.type ? atte.permittedUse.type : '',
    maxCapacity:
      atte.permittedUse && atte.permittedUse.maxSpaceCapacity
        ? atte.permittedUse.maxSpaceCapacity
        : 0,
    attendanceWarnings: atte.attendanceWarnings,
    isNapping: atte.isNapping,
    children: atte.teams.reduce((acc: ITeamNode[], team: ITeam) => {
      const teamNode = {
        id: team.id,
        spaceId: atte.space.id,
        taskId: atte.space.taskId,
        attendanceId: atte.id,
        label: team.name,
        ratio: team.teamRatio?.standardRatio,
        ratioLicense: team.teamRatio?.licenseType,
        ratioName: team.teamRatio?.name,
        teamWarnings: team.teamWarnings,
        maxCapacity: team.teamRatio ? team.teamRatio.maxTeamSize : 0,
        disabled: false,
        tickable: team.attendees.length > 0,
        type: 'team',
        selected: false,
        children: [] as IAttendeeNode[],
      }

      const attendeeNodes = team.attendees.reduce(
        (acc: IAttendeeNode[], entity: IAttendee) => {
          entity.type === ATTENDEE_TYPES.staff
            ? attendStaffIds.push(entity.id as string)
            : attendStudentIds.push(entity.id as string)
          acc.push({
            id: entity.id as string,
            familyId: entity.familyId,
            label: entity.preferredName,
            avatar: entity.avatar as string,
            preferredName: entity.preferredName,
            firstName: entity.firstName,
            lastName: entity.lastName,
            middleName: entity.middleName as string,
            dateOfBirth: entity.dateOfBirth as string,
            medications: entity.medications,
            medicalConditions: entity.medicalConditions,
            prohibitedFoods: entity.prohibitedFoods,
            type: entity.type as string,
            selected: false,
            group: entity.group
              ? {
                  id: entity.group.id,
                  name: entity.group.name,
                }
              : null,
            school: entity.school
              ? {
                  id: entity.school.id,
                }
              : null,
            grade: entity.grade,
            pod: entity.pod,
          })
          return acc
        },
        [],
      )

      attendeeNodes.sort((a, b) => {
        if (a.type && b.type) {
          const typeA = a.type.toLowerCase()
          const typeB = b.type.toLowerCase()
          if (typeA < typeB) {
            return -1
          }
          if (typeA > typeB) {
            return 1
          }

          if (a.preferredName && b.preferredName) {
            const nameA = a.preferredName.toLowerCase()
            const nameB = b.preferredName.toLowerCase()
            if (nameA < nameB) {
              return -1
            }
            if (nameA > nameB) {
              return 1
            }
          }

          return 0
        }

        return 0
      })

      teamNode.children = attendeeNodes
      acc.push(teamNode)
      return acc
    }, []),
  }

  attendanceNode.tickable =
    attendanceNode?.children?.some((att) => att.children.length > 0) ?? false

  return {
    attendanceNode,
    attendStaffIds,
    attendStudentIds,
  }
}
